import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Car, LogIn, UserPlus, Eye, EyeOff, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [resetting, setResetting] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
      return;
    }

    // Check if admin exists
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API}/admin/check`);
        setIsRegister(!response.data.exists);
      } catch (error) {
        console.error("Error checking admin:", error);
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAdmin();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? "/admin/register" : "/admin/login";
      const response = await axios.post(`${API}${endpoint}`, formData);

      localStorage.setItem("admin_token", response.data.access_token);
      toast.success(isRegister ? "Account created successfully!" : "Login successful!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Authentication failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetAdmin = async () => {
    if (!resetCode.trim()) {
      toast.error("Please enter the reset code");
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/admin/reset`, { reset_code: resetCode });
      toast.success("Admin reset successful! You can now create a new admin account.");
      setResetDialogOpen(false);
      setResetCode("");
      setIsRegister(true);
      setFormData({ username: "", password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid reset code. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="w-12 h-12 bg-primary flex items-center justify-center">
            <Car className="w-7 h-7 text-white" />
          </div>
          <div className="text-left">
            <span className="font-heading font-bold text-xl text-white tracking-tight">
              Easy Payments
            </span>
            <span className="block text-xs text-slate-400 uppercase tracking-widest">
              Admin Portal
            </span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white p-8">
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
            {isRegister ? "Create Admin Account" : "Admin Login"}
          </h1>
          <p className="text-slate-500 text-sm mb-8">
            {isRegister
              ? "Set up your admin credentials to manage vehicles."
              : "Enter your credentials to access the dashboard."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-slate-700 font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                className="mt-2 form-input"
                placeholder="Enter username"
                required
                data-testid="username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="form-input pr-10"
                  placeholder="Enter password"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPassword(!showPassword);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white uppercase tracking-wide font-bold py-6 btn-brutalist"
              data-testid="submit-btn"
            >
              {loading ? (
                "Please wait..."
              ) : isRegister ? (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Account
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>

            {/* Forgot Password link - only show on login */}
            {!isRegister && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setResetDialogOpen(true)}
                  className="text-sm text-slate-500 hover:text-primary transition-colors duration-200"
                  data-testid="forgot-password-btn"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Back to Site */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-slate-500 hover:text-white text-sm transition-colors duration-200"
            data-testid="back-to-site"
          >
            &larr; Back to main site
          </a>
        </div>
      </div>

      {/* Reset Admin Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Reset Admin Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600 text-sm">
              Enter the admin reset code to remove the current admin account. 
              You&apos;ll then be able to create a fresh admin account with new credentials.
            </p>
            <div>
              <Label htmlFor="reset-code">Reset Code</Label>
              <Input
                id="reset-code"
                type="password"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="Enter reset code"
                className="mt-2"
                data-testid="reset-code-input"
              />
            </div>
            <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 p-3">
              <strong>Warning:</strong> This will delete the current admin account. 
              Vehicles will not be affected.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetDialogOpen(false);
                setResetCode("");
              }}
              data-testid="cancel-reset-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetAdmin}
              disabled={resetting}
              className="bg-primary text-white"
              data-testid="confirm-reset-btn"
            >
              {resetting ? "Resetting..." : "Reset Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLoginPage;
